import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useMediaQuery,
  Box,
  IconButton,
  useTheme,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { remixerApi } from 'services/api/remixer';
import {
  FormulaField,
  FormulaFields,
  FormulaFieldValue,
} from 'services/store/remixer/types';
import remixer from 'services/locales/en/remixer';
import { useAuthStore } from 'services/hooks';
import { locations } from 'services/routes/locations';
import { checkCurrentSongLoading } from 'services/store/remixer/selectors';
import aiMusicGeneratorApi from 'services/api/aiMusicGenerator/aiMusicGeneratorApi';
import useAnalytics from 'services/hooks/analytics/useAnalytics';
import { events } from 'services/analytics';
import FormulaItemDialog from './FormulaItemDialog';
import { defaultValues, components, icons } from './helpers';
import {
  FormulaButton,
  DialogButtonsContainer,
  CancelButton,
  ApplyButton,
} from './styles';

interface FormulaItemProps {
  field: FormulaFields;
}

const FormulaItem = ({ field }: FormulaItemProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { track } = useAnalytics();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const [value, setValue] = useState<FormulaFieldValue>(defaultValues[field]);
  const { data: genres = [] } =
    aiMusicGeneratorApi.useGetAiMusicGeneratorGenresQuery();
  const { authStore } = useAuthStore();
  const { isLoggedIn } = authStore;
  const [remixSong] = remixerApi.useRemixSongMutation();

  const submitHandler = () => {
    if (!isLoggedIn) {
      navigate(locations.general.register.pathname(''));
    }
    remixSong({ field, value } as FormulaField);

    track(events.studioRemixApplied(field));
  };

  useEffect(() => {
    if (field !== FormulaFields.Genre) {
      return;
    }
    if (genres.length && !value) {
      setValue(genres[0].name);
    }
  }, [field, genres, value, setValue]);

  const isLoading = useSelector(checkCurrentSongLoading);

  return (
    <Box>
      {isMobile ? (
        <IconButton onClick={openModal} size="large" disabled={isLoading}>
          {icons[field]}
        </IconButton>
      ) : (
        <FormulaButton
          onClick={openModal}
          startIcon={icons[field]}
          disabled={isLoading}
        >
          {t(remixer.formulaFieldLabels[field])}
        </FormulaButton>
      )}
      <FormulaItemDialog open={isModalOpen} onClose={closeModal}>
        <Typography sx={{ pb: theme.spacing(2), fontWeight: 500 }}>
          {t(remixer.formulaFieldLabels[field])}
        </Typography>
        <Box>
          <Box
            component={components[field]}
            value={value}
            onChange={setValue}
          />
          <DialogButtonsContainer>
            <CancelButton onClick={closeModal}>
              {t('remixer:formulaDialogAction:cancel')}
            </CancelButton>
            <ApplyButton
              variant="contained"
              color="primary"
              onClick={() => {
                submitHandler();
                closeModal();
              }}
            >
              {t('remixer:formulaDialogAction:apply')}
            </ApplyButton>
          </DialogButtonsContainer>
        </Box>
      </FormulaItemDialog>
    </Box>
  );
};

export default FormulaItem;
